import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { parseAsLocalDate } from '../../../shared/_utils/date-utils';
import { Form8865TextualService } from './form8865-textual.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-form8865-textual',
    templateUrl: './form8865-textual.component.html',
    styleUrls: ['./form8865-textual.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Form8865TextualComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Modal properties
  modalTitle = 'Form 8865- Information Return of U.S. Persons With Respect To Certain Foreign Partnerships';
  minimise = false;
  modal_name: string | null = null;
  crudLoading = false;
  showFormLoader = false;

  // Data from AngularJS
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};

  // Form data
  attribData: { [key: string]: any } = {};
  irsF8865ScheduleNAData: any[] = [];
  irsF8865ScheduleOData: any[] = [];
  schedulePG1Data: any[] = [];

  // Lookup data
  filingGroup: any[] = [];
  countries: any[] = [];
  states: any[] = [];
  currencies: any[] = [];
  PrincipalBusinessActivity: any[] = [];
  partnerDetails: any[] = [];

  // Selected values
  selectedGroup: any = null;
  selectedGroup1: any = null;
  otherPartner: any = null;
  countryVar: string = '';
  countryCust: string = '';
  countryAgent: string = '';

  // Checkbox states - initialize as empty/false, will be set from data
  checkBoxb1: boolean = false;
  checkBoxb3: boolean = false;
  checkBoxb4: boolean = false;
  checkBoxb5: boolean = false;
  checkCatg1: boolean = false;
  checkCatg2: boolean = false;
  checkConstOwner: boolean = false;
  form_1042: boolean = false;
  form_8804: boolean = false;
  form_1065: boolean = false;

  // Radio button states
  radioBlk: string = '';
  radioUnblk: string = '';
  radioUnblck: string = '';
  
  // H.3 and H.4 radio states
  radioH3Type: string = '';
  radioH4Type: string = '';

  // Initialization flags
  private isInitialized = false;
  private isLoading = false;
  private lastContextKey = '';
  private readonly subscriptions = new Subscription();

  constructor(
    private form8865Service: Form8865TextualService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.initIfContextChanged();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.initIfContextChanged();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    this.filterParams = fp;
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url ?? gc.api ?? '' };
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    this.globalService = {
      ...extractObject(this.gtwGlobal.global?.globalParams || {}),
      ...extractObject(hydrated)
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    if (Object.keys(rowObj).length) {
      this.rowData = rowObj;
    }
  }

  private buildContextKey(): string {
    const ty = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || '';
    const sc = this.filterParams?.scenario || this.globalService?.scenario || '';
    const ck = this.rowData?.COMBINATION_KEY || this.rowData?.HO_COMBINATION_KEY || '';
    return `${ty}|${sc}|${ck}`;
  }

  private initIfContextChanged(): void {
    const key = this.buildContextKey();
    if (key === this.lastContextKey) return;
    this.isInitialized = false;
    this.isLoading = false;
    this.tryInit();
  }

  private tryInit() {
    if (this.isInitialized || this.isLoading) return;

    if (this.rowData && Object.keys(this.rowData).length > 0 &&
        this.GENERAL_CONFIG && (this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api_url)) {
      this.lastContextKey = this.buildContextKey();
      this.isLoading = true;
      this.modal_name = 'form8865-textual';
      this.initLoadData();
    }
  }

  private getBaseUrl(): string {
    return this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api_url || '';
  }

  // Get correct tax_year - prefer filterParams (user-selected in UI), then rowData, then globalService
  private getCorrectTaxYear(): string {
    return this.filterParams.tax_year || this.rowData.TAX_YEAR || this.globalService.tax_year;
  }

  // Get correct scenario - prefer filterParams, then checkLockedbj, then globalService
  private getCorrectScenario(): any {
    // First try filterParams (user-selected in UI)
    if (this.filterParams.scenario) {
      return this.filterParams.scenario;
    }
    // Then try checkLockedbj
    if (this.globalService.checkLockedbj) {
      try {
        const checkLocked = safeJsonInput<any>(this.globalService.checkLockedbj, null, 'form8865-textual.parse1');
        if (Array.isArray(checkLocked) && checkLocked.length > 0 && checkLocked[0].scenario_key) {
          return checkLocked[0].scenario_key;
        }
      } catch (e) {
        // Continue to fallback
      }
    }
    return this.globalService.scenario;
  }

  initLoadData() {
    this.showFormLoader = true;
    // Load filing group first, then cascade load other data
    this.loadFilingGroup();
  }

  loadFilingGroup() {
    // Use full globalService params merged with filterParams - exactly like original AngularJS
    // Original: var param = _.merge({}, GlobalService.globalParams, filterParams);
    
    const tax_year = this.getCorrectTaxYear();
    const scenario = this.getCorrectScenario();
    
    const params = {
      ...this.globalService,
      ...this.filterParams,
      // Override with corrected values from reliable sources
      tax_year: tax_year,
      scenario: scenario
    };

    this.form8865Service.getFilingGroup(
      this.getBaseUrl(),
      params,
      {}, // globalService already merged into params
      {}  // filterParams already merged into params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.filingGroup = data.jsonObject || [];
          // Load other lookup data in parallel
          this.loadLookupData();
          this.loadFormData();
          this.loadSchedulePG1Data();
          this.loadScheduleOData();
          this.loadFilerInfo();
          this.loadPartnerInfo();
        } else {
          this.showFormLoader = false;
          this.isLoading = false;
        }
      },
      (error: any) => {
        console.error('Error loading filing group:', error);
        this.showFormLoader = false;
        this.isLoading = false;
      }
    );
  }

  loadLookupData() {
    const params = {
      tax_year: this.getCorrectTaxYear(),
      jcd_key: this.globalService.jcd_key || 250
    };

    // Load countries
    this.form8865Service.getCountries(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data.callSuccess === '1') {
        this.countries = data.jsonObject || [];
      }
    });

    // Load states
    this.form8865Service.getStates(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data.callSuccess === '1') {
        this.states = data.jsonObject || [];
      }
    });

    // Load currencies
    this.form8865Service.getCurrencies(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.currencies = data.jsonObject || [];
          // Force change detection after currencies are loaded
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading currencies:', error);
      }
    );

    // Load principal business activities
    this.form8865Service.getPrincipalBusinessActivity(
      this.getBaseUrl(),
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data.callSuccess === '1') {
        this.PrincipalBusinessActivity = data.jsonObject || [];
        
        // After PBA list loads, set selectedGroup1 if we have a saved value in attribData
        // This handles the race condition where processFormData runs before PBA list is ready
        const savedG6Code = this.attribData['8865_NA_G6_PRIPL_ACTIVE_CODE'];
        if (savedG6Code && !this.selectedGroup1) {
          const pba = this.PrincipalBusinessActivity.find(x => x.ACTIVITY_CODE == savedG6Code);
          if (pba) {
            this.selectedGroup1 = pba;
            this.cdr.detectChanges();
          }
        }
      }
    });

    // Load partner details
    this.form8865Service.getPartnerDetails(
      this.getBaseUrl(),
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data.callSuccess === '1') {
        this.partnerDetails = _.filter(data.jsonObject || [], { COMBINATION_KEY: this.rowData.COMBINATION_KEY });
        
        // After partnerDetails loads, set otherPartner if we have a saved value in attribData
        // This handles the race condition where processFormData runs before partnerDetails is ready
        const savedOtherPartner = this.attribData['8865_NA_OTHER_PARTNER'];
        if (savedOtherPartner && !this.otherPartner) {
          const partner = this.partnerDetails.find(x => x.LE_KEY == savedOtherPartner);
          if (partner) {
            this.otherPartner = partner;
            this.cdr.detectChanges();
          }
        }
      }
    });
  }

  loadFormData() {
    const params = {
      tax_year: this.getCorrectTaxYear(),
      scenario: this.getCorrectScenario(),
      jcd_key: this.globalService.jcd_key || 250,
      irs_form_no: 'F8865',
      combination_key: this.rowData.HO_COMBINATION_KEY
    };

    this.form8865Service.getFormData(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1') {
          this.irsF8865ScheduleNAData = data.jsonObject || [];
          this.processFormData();
          this.isInitialized = true;
        }
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading form data:', error);
        this.showFormLoader = false;
        this.isLoading = false;
      }
    );
  }

  loadFilerInfo() {
    const params = {
      tax_year: this.getCorrectTaxYear(),
      scenario: this.getCorrectScenario(),
      jcd_key: this.globalService.jcd_key || 250,
      combination_key: this.rowData.COMBINATION_KEY
    };

    this.form8865Service.getFilerInfo(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject && data.jsonObject.length > 0) {
          const filerData = data.jsonObject[0];
          // Set dates if not already set - use parseAsLocalDate to avoid timezone shift issues
          if (!this.attribData['8865_NA_FILER_BEGIN_DATE'] && filerData.F8865_NA_FILER_BEGIN_DATE) {
            this.attribData['8865_NA_FILER_BEGIN_DATE'] = parseAsLocalDate(filerData.F8865_NA_FILER_BEGIN_DATE);
          }
          if (!this.attribData['8865_NA_FILER_END_DATE'] && filerData.F8865_NA_FILER_END_DATE) {
            this.attribData['8865_NA_FILER_END_DATE'] = parseAsLocalDate(filerData.F8865_NA_FILER_END_DATE);
          }
          
          // Set selected filing group if not already set - try multiple matching strategies
          if (!this.selectedGroup) {
            let fg = null;
            
            // Strategy 1: Match by LEGAL_ENTITY_ID (loose comparison)
            fg = this.filingGroup.find(x => x.LEGAL_ENTITY_ID == filerData.F8865_NA_FILER_LEGAL_ENTITY_ID);
            
            // Strategy 2: Match by LEGAL_ENTITY_NAME if ID not found
            if (!fg && filerData.LEGAL_ENTITY_ID) {
              fg = this.filingGroup.find(x => x.LEGAL_ENTITY_NAME == filerData.LEGAL_ENTITY_ID);
            }
            
            // Strategy 3: Match by F8865_NA_FILER_NAME_1
            if (!fg && filerData.F8865_NA_FILER_NAME_1) {
              fg = this.filingGroup.find(x => x.LEGAL_ENTITY_NAME == filerData.F8865_NA_FILER_NAME_1);
            }
            
            if (fg) {
              this.selectedGroup = fg;
            } else {
              console.warn('No matching filing group found! Available:', 
                this.filingGroup.slice(0, 5).map(f => ({ id: f.LEGAL_ENTITY_ID, name: f.LEGAL_ENTITY_NAME })));
            }
          }
          
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading filer info:', error);
      }
    );
  }

  loadPartnerInfo() {
    const params = {
      tax_year: this.getCorrectTaxYear(),
      scenario: this.getCorrectScenario(),
      jcd_key: this.globalService.jcd_key || 250,
      combination_key: this.rowData.COMBINATION_KEY
    };

    this.form8865Service.getPartnerInfo(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject && data.jsonObject.length > 0) {
          const partnerData = data.jsonObject[0];
          
          // Set partnership dates - use parseAsLocalDate to avoid timezone shift issues
          // These are the "Beginning period" and "Ending period" fields
          if (!this.attribData['8865_NA_PTSP_BEGIN_DATE'] && partnerData.F8865_NA_PTSP_BEGIN_DATE) {
            this.attribData['8865_NA_PTSP_BEGIN_DATE'] = parseAsLocalDate(partnerData.F8865_NA_PTSP_BEGIN_DATE);
          }
          if (!this.attribData['8865_NA_PTSP_END_DATE'] && partnerData.F8865_NA_PTSP_END_DATE) {
            this.attribData['8865_NA_PTSP_END_DATE'] = parseAsLocalDate(partnerData.F8865_NA_PTSP_END_DATE);
          }
          
          // Set partnership name
          if (!this.attribData['8865_NA_PTSP_NAME_1'] && partnerData.F8865_NA_PTSP_NAME_1) {
            this.attribData['8865_NA_PTSP_NAME_1'] = partnerData.F8865_NA_PTSP_NAME_1;
          }
          
          // Set EIN
          if (!this.attribData['8865_NA_PTSP_EIN'] && partnerData.F8865_NA_PTSP_EIN) {
            this.attribData['8865_NA_PTSP_EIN'] = partnerData.F8865_NA_PTSP_EIN;
          }
          
          // Set country under whose laws organized
          if (!this.attribData['8865_NA_G3_COUNTRY'] && partnerData.F8865_NA_G3_COUNTRY) {
            this.attribData['8865_NA_G3_COUNTRY'] = partnerData.F8865_NA_G3_COUNTRY;
          }
          
          // Set date of organization - use parseAsLocalDate to avoid timezone shift issues
          if (!this.attribData['8865_NA_G4_DATE'] && partnerData.F8865_NA_G4_DATE) {
            this.attribData['8865_NA_G4_DATE'] = parseAsLocalDate(partnerData.F8865_NA_G4_DATE);
          }
          
          // Set principal place
          if (!this.attribData['8865_NA_G5_PRIPL_PLACE'] && partnerData.F8865_NA_G5_PRIPL_PLACE) {
            this.attribData['8865_NA_G5_PRIPL_PLACE'] = partnerData.F8865_NA_G5_PRIPL_PLACE;
          }
          
          // Set principal business activity
          // Matches AngularJS: if(Object.keys(vm.selectedGroup1).length === 0)
          // Only set from partnerInfo if NO saved value exists in attribData
          if (!this.attribData['8865_NA_G6_PRIPL_ACTIVE_CODE']) {
            const pba = this.PrincipalBusinessActivity.find(x => x.ACTIVITY_CODE == partnerData.F8865_NA_G6_PRIPL_ACTIVE_CODE);
            if (pba) {
              this.selectedGroup1 = pba;
              this.attribData['8865_NA_G6_PRIPL_ACTIVE_CODE'] = pba.ACTIVITY_CODE;
            }
          }
          
          // Original AngularJS uses: !vm.attribData['8865_NA_G8_FUNC_CURR'] ? data.jsonObject[0].F8865_NA_G8_FUNC_CURR : vm.attribData['8865_NA_G8_FUNC_CURR']
          // This means: if current value is falsy (empty string, undefined, null, 0), use API value
          if (!this.attribData['8865_NA_G8_FUNC_CURR']) {
            this.attribData['8865_NA_G8_FUNC_CURR'] = partnerData.F8865_NA_G8_FUNC_CURR;
          }
          if (!this.attribData['8865_NA_G8_EXCH_RATE']) {
            this.attribData['8865_NA_G8_EXCH_RATE'] = partnerData.F8865_NA_G8_EXCH_RATE;
          }
          
          // Set reference ID
          if (!this.attribData['8865_NA_G2_REF_ID'] && partnerData.F8865_NA_G2_REF_ID) {
            this.attribData['8865_NA_G2_REF_ID'] = partnerData.F8865_NA_G2_REF_ID;
          }
          
          // Set country and address
          if (!this.countryVar) {
            if (partnerData.COUNTRY_ORIGIN === 'FOR') {
              this.countryVar = partnerData.F8865_NA_PTSP_F_COUNTRY || '';
              this.attribData['8865_NA_PTSP_F_ADDR_1'] = partnerData.F8865_NA_PTSP_F_ADDR_1;
              this.attribData['8865_NA_PTSP_F_ADDR_2'] = partnerData.F8865_NA_PTSP_F_ADDR_2;
              this.attribData['8865_NA_PTSP_F_CITY'] = partnerData.F8865_NA_PTSP_F_CITY;
              this.attribData['8865_NA_PTSP_F_STATE'] = partnerData.F8865_NA_PTSP_F_STATE;
              this.attribData['8865_NA_PTSP_F_ZIP'] = partnerData.F8865_NA_PTSP_F_ZIP;
            } else if (partnerData.COUNTRY_ORIGIN === 'US') {
              this.countryVar = partnerData.F8865_NA_PTSP_US_COUNTRY || 'US';
              this.attribData['8865_NA_PTSP_US_ADDR_1'] = partnerData.F8865_NA_PTSP_US_ADDR_1;
              this.attribData['8865_NA_PTSP_US_ADDR_2'] = partnerData.F8865_NA_PTSP_US_ADDR_2;
              this.attribData['8865_NA_PTSP_US_CITY'] = partnerData.F8865_NA_PTSP_US_CITY;
              this.attribData['8865_NA_PTSP_US_STATE'] = partnerData.F8865_NA_PTSP_US_STATE;
              this.attribData['8865_NA_PTSP_US_ZIP'] = partnerData.F8865_NA_PTSP_US_ZIP;
            }
          }
          
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading partner info:', error);
      }
    );
  }

  loadSchedulePG1Data() {
    const params = {
      tax_year: this.getCorrectTaxYear(),
      scenario: this.getCorrectScenario(),
      jcd_key: this.globalService.jcd_key || 250,
      irs_form_no: 'F8865',
      combination_key: this.rowData.HO_COMBINATION_KEY
    };

    this.form8865Service.getSchedulePG1Data(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject) {
          this.processSchedulePG1Data(data.jsonObject);
        }
      },
      (error: any) => {
        console.error('Error loading schedule PG1 data:', error);
      }
    );
  }

  loadScheduleOData() {
    const params = {
      tax_year: this.getCorrectTaxYear(),
      scenario: this.getCorrectScenario(),
      jcd_key: this.globalService.jcd_key || 250,
      irs_form_no: 'F8865',
      combination_key: this.rowData.HO_COMBINATION_KEY
    };

    this.form8865Service.getScheduleOData(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {  
        if (data.callSuccess === '1' && data.jsonObject) {
          this.irsF8865ScheduleOData = data.jsonObject;
          this.processScheduleOData();
        }
      },
      (error: any) => {
        console.error('Error loading schedule O data:', error);
      }
    );
  }

  processFormData() {
    // Reset checkbox states - will be set from data
    this.checkBoxb1 = false;
    this.checkBoxb3 = false;
    this.checkBoxb4 = false;
    this.checkBoxb5 = false;
    this.checkCatg1 = false;
    this.checkCatg2 = false;
    this.checkConstOwner = false;
    this.form_1042 = false;
    this.form_8804 = false;
    this.form_1065 = false;

    for (let i = 1; i < this.irsF8865ScheduleNAData.length; i++) {
      const item = this.irsF8865ScheduleNAData[i];
      
      if (!item.object_id || !item.attrib_value) {
        continue;
      }
      
      const tempString = item.object_id;
      const tempValue = item.attrib_value;
      const attribName = tempValue.split('~')[0];
      const attribValue = (item.VALUE === '0') ? '' : item.VALUE;
      const updateFlag = item.OVERRIDE_FLAG;

      // Update the data structure
      this.irsF8865ScheduleNAData[i] = {
        i: item,
        tax_year: tempString.split('~')[0],
        trans_type_key: tempString.split('~')[1],
        attrib_name: attribName,
        attrib_desc: tempValue.split('~')[3],
        line_attrib_key: tempValue.split('~')[1],
        trans_details_key: tempString.split('~')[3],
        occurrence: tempString.split('~')[4],
        VALUE: item.VALUE,
        form_key: tempString.split('~')[6],
        propertyType: String(item.PROPERTY_TYPE || ''),
        is_update: updateFlag
      };

      // Process specific attributes
      this.processAttribute(attribName, attribValue);
    }
  }

  processAttribute(attribName: string, attribValue: any) {
    // Handle dates - use parseAsLocalDate to avoid timezone shift issues
    if (['8865_NA_FILER_BEGIN_DATE', '8865_NA_FILER_END_DATE', '8865_NA_G4_DATE', 
         '8865_NA_PTSP_BEGIN_DATE', '8865_NA_PTSP_END_DATE'].includes(attribName)) {
      this.attribData[attribName] = parseAsLocalDate(attribValue);
    }
    // Handle country selection for partnership address
    else if (attribName === '8865_NA_PTSP_US_COUNTRY' && attribValue === 'US') {
      this.countryVar = attribValue;
      this.attribData['8865_NA_PTSP_US_COUNTRY'] = attribValue;
    }
    else if (attribName === '8865_NA_PTSP_F_COUNTRY' && attribValue && attribValue !== 'US') {
      this.countryVar = attribValue;
      this.attribData['8865_NA_PTSP_F_COUNTRY'] = attribValue;
    }
    // Handle country for agent
    else if (attribName === '8865_NA_FPTSP_AGENT_US_COUNTRY' && attribValue === 'US') {
      this.countryAgent = attribValue;
      this.attribData['8865_NA_FPTSP_AGENT_US_COUNTRY'] = attribValue;
    }
    else if (attribName === '8865_NA_FPTSP_AGENT_F_COUNTRY' && attribValue && attribValue !== 'US') {
      this.countryAgent = attribValue;
      this.attribData['8865_NA_FPTSP_AGENT_F_COUNTRY'] = attribValue;
    }
    // Handle country for custodian
    else if (attribName === '8865_NA_PERSONS_BOOK_US_COUNTRY' && attribValue === 'US') {
      this.countryCust = attribValue;
      this.attribData['8865_NA_PERSONS_BOOK_US_COUNTRY'] = attribValue;
    }
    else if (attribName === '8865_NA_PERSONS_BOOK_F_COUNTRY' && attribValue && attribValue !== 'US') {
      this.countryCust = attribValue;
      this.attribData['8865_NA_PERSONS_BOOK_F_COUNTRY'] = attribValue;
    }
    // Handle filing group - use loose comparison (==) for type flexibility
    else if (attribName === '8865_NA_FILER_LEGAL_ENTITY_ID') {
      this.attribData[attribName] = attribValue;
      
      if (!this.selectedGroup) {
        // Try to find by LEGAL_ENTITY_ID first
        let fg = this.filingGroup.find(x => x.LEGAL_ENTITY_ID == attribValue);
        
        // If not found, try matching by LEGAL_ENTITY_NAME (in case attribValue is actually a name)
        if (!fg) {
          fg = this.filingGroup.find(x => x.LEGAL_ENTITY_NAME == attribValue);
        }

        if (fg) {
          this.selectedGroup = fg;
        }
      }
    }
    // Handle filer name - can also be used to find filing group
    else if (attribName === '8865_NA_FILER_NAME_1') {
      this.attribData[attribName] = attribValue;
      if (!this.selectedGroup && attribValue) {
        const fg = this.filingGroup.find(x => x.LEGAL_ENTITY_NAME == attribValue);
        if (fg) {
          this.selectedGroup = fg;
        }
      }
    }
    // Handle partner
    else if (attribName === '8865_NA_OTHER_PARTNER') {
      // ALWAYS store the value in attribData (for race condition handling)
      this.attribData['8865_NA_OTHER_PARTNER'] = attribValue;
      
      if (attribValue) {
        const partner = this.partnerDetails.find(x => x.LE_KEY == attribValue);
        if (partner) {
          this.otherPartner = partner;
        }
      }
    }
    // Handle principal business activity
    // Matches AngularJS: sets selectedGroup1 from saved value AND stores in attribData
    else if (attribName === '8865_NA_G6_PRIPL_ACTIVE_CODE') {
      // ALWAYS store the value in attribData (this was missing before!)
      this.attribData['8865_NA_G6_PRIPL_ACTIVE_CODE'] = attribValue;
      
      if (attribValue) {
        const pba = this.PrincipalBusinessActivity.find(x => x.ACTIVITY_CODE == attribValue);
        if (pba) {
          // Saved value takes precedence over partnerInfo (remove !this.selectedGroup1 condition)
          this.selectedGroup1 = pba;
        }
      }
    }
    // Handle checkboxes for category (A section) - only set to true if value is '1'
    else if (attribName === '8865_NA_A_1_Y' && attribValue === '1') {
      this.checkBoxb1 = true;
    }
    else if (attribName === '8865_NA_A_2_Y' && attribValue === '1') {
      this.checkBoxb3 = true;
    }
    else if (attribName === '8865_NA_A_3_Y' && attribValue === '1') {
      this.checkBoxb4 = true;
    }
    else if (attribName === '8865_NA_A_4_Y' && attribValue === '1') {
      this.checkBoxb5 = true;
    }
    // Handle form checkboxes (H.2 section)
    else if (attribName === '8865_NA_F1042_Y' && attribValue === '1') {
      this.form_1042 = true;
    }
    else if (attribName === '8865_NA_F8804_Y' && attribValue === '1') {
      this.form_8804 = true;
    }
    else if (attribName === '8865_NA_F1065_Y' && attribValue === '1') {
      this.form_1065 = true;
    }
    // Handle partner category checkboxes (F.4 section)
    else if (attribName === '8865_NA_PARTNER_CATG_1_Y' && attribValue === '1') {
      this.checkCatg1 = true;
    }
    else if (attribName === '8865_NA_PARTNER_CATG_2_Y' && attribValue === '1') {
      this.checkCatg2 = true;
    }
    else if (attribName === '8865_NA_PARTNER_CONSTRUCTIVE_OWNER_Y' && attribValue === '1') {
      this.checkConstOwner = true;
    }
    // Handle H.3 and H.4 person/business type (values: P=Person, B=Business, N=Not Applicable)
    else if (attribName === '8865_NA_H3_FLAG') {
      this.radioH3Type = attribValue || '';
      this.attribData['8865_NA_H3_FLAG'] = attribValue;
    }
    else if (attribName === '8865_NA_H4_FLAG') {
      this.radioH4Type = attribValue || '';
      this.attribData['8865_NA_H4_FLAG'] = attribValue;
    }
    // General attribute storage
    else {
      this.attribData[attribName] = attribValue;
    }
  }

  processSchedulePG1Data(rawData: any[]) {
    this.schedulePG1Data = [];
    
    if (!rawData || rawData.length === 0) return;

    let i = 1;
    let previous_line_no = rawData[i]?.line_no;

    while (i < rawData.length) {
      const tempObj: any = {
        isUpdate: 'N',
        isEditableFlag: 'Y',
        value: []
      };

      let j = i;
      let count = 0;

      while (j < rawData.length && previous_line_no === rawData[j].line_no) {
        const item = rawData[j];
        const attribParts = item.attrib_value?.split('~') || [];
        const attribName = attribParts[0];

        if (attribName === 'LINE_NO') {
          tempObj.propertyType = String(item.PROPERTY_TYPE || '');
          tempObj.line_no = item.line_no;
          tempObj.saveParams = {
            tax_year: item.object_id?.split('~')[0],
            trans_type_key: item.object_id?.split('~')[1],
            trans_details_key: item.object_id?.split('~')[3],
            form_key: item.object_id?.split('~')[6],
            occurrence: item.object_id?.split('~')[4]
          };
        } else if (attribName === 'LINE_DESCRIPTION') {
          tempObj.line_description = attribParts[3] || '';
        } else if (attribName === '8865_NA_H5_AMT') {
          tempObj.line_description1 = attribParts[3] || '';
          tempObj.isEditableFlagText = 'Y';
          tempObj.value_1 = {
            VALUE: item.VALUE,
            line_attrib_key: attribParts[1],
            attrib_name: attribName
          };
        } else {
          tempObj.propertyType = String(item.PROPERTY_TYPE || '');
          
          if (String(item.PROPERTY_TYPE) === '13') {
            // Yes/No radio buttons - matches AngularJS logic exactly
            // AngularJS: first item sets attrib_name as string, second converts both to arrays
            if (count > 0) {
              if (count === 1) {
                // Convert string to array (matching AngularJS line 894-896)
                tempObj.attrib_name = [tempObj.attrib_name];
                tempObj.value = [tempObj.value];
              }
              // Add to arrays (matching AngularJS line 898-902)
              tempObj.attrib_name[count] = attribName;
              tempObj.value[count] = {
                VALUE: item.VALUE,
                line_attrib_key: attribParts[1]
              };
            } else {
              // First item - set as string/object (matching AngularJS line 904-911)
              tempObj.isEditableFlag = 'Y';
              tempObj.attrib_name = attribName;
              tempObj.value = {
                VALUE: item.VALUE,
                line_attrib_key: attribParts[1]
              };
            }
            count++;
          } else {
            // Regular value (non-radio button rows like line 8A, 9, etc.)
            tempObj.isEditableFlag = 'Y';
            tempObj.attrib_name = attribName;
            tempObj.value = {
              VALUE: item.VALUE,
              line_attrib_key: attribParts[1]
            };
          }
        }

        previous_line_no = item.line_no;
        j++;
      }

      if (j < rawData.length) {
        previous_line_no = rawData[j].line_no;
      }

      this.schedulePG1Data.push(tempObj);
      i = j;
    }

    
    // Call setAmt() to load line 11 values from M2_CHK - matching original AngularJS
    this.setAmt();
    
    // Apply initial validation - matching ng-init behavior in AngularJS
    // In AngularJS, ng-init calls checkingForValidation for each row during render
    // When it reaches line 10a, it sets 10b and 11 to disabled
    this.applyInitialValidation();
    
    this.cdr.detectChanges();
  }

  /**
   * Apply initial validation after data is loaded
   * This matches the ng-init="ctrl.checkingForValidation(row, $index)" behavior in AngularJS
   * When data loads, lines 10b and 11 should be disabled based on line 10a
   */
  applyInitialValidation() {
    // Find line 10a - if it exists, disable 10b and 11
    // This matches the original AngularJS behavior where ng-init for 10a sets 10b and 11 to disabled
    const line10a = this.schedulePG1Data.find((r: any) => r.line_no === '10a');
    
    if (line10a) {
      // Always disable 10b and 11 when 10a exists (matching AngularJS ng-init behavior)
      const line10b = this.schedulePG1Data.find((r: any) => r.line_no === '10b');
      const line11 = this.schedulePG1Data.find((r: any) => r.line_no === '11');
      
      if (line10b) {
        line10b.isEditableFlag = 'N';
      }
      if (line11) {
        line11.isEditableFlag = 'N';
      }
      
      // If line 10a has a value, also disable line 10a itself
      // This matches what the user observed - when value comes from API, 10a is also disabled
      if (line10a.value && Array.isArray(line10a.value)) {
        const hasValue = line10a.value.some((v: any) => v && v.VALUE);
        if (hasValue) {
          line10a.isEditableFlag = 'N';
        }
      }
    }
    
    // Also check line 5 - if No is selected, disable the amount field
    const line5 = this.schedulePG1Data.find((r: any) => r.line_no === '5');
    if (line5 && line5.value && Array.isArray(line5.value)) {
      if (line5.value[1]?.VALUE === 'N') {
        line5.isEditableFlagText = 'N';
        if (line5.value_1) {
          line5.value_1.VALUE = null;
        }
      }
    }
  }

  // Load M2 check data for line 11 - matching original AngularJS setAmt() function
  setAmt() {
    const params = {
      tax_year: this.getCorrectTaxYear(),
      scenario: this.getCorrectScenario(),
      combination_key: this.rowData.COMBINATION_KEY,
      jcd_key: this.globalService.jcd_key || 250
    };

    this.form8865Service.getM2CheckData(
      this.getBaseUrl(),
      params,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject && data.jsonObject.length > 0) {
          // Find line 11 in schedulePG1Data (index 7 in original, but we find by line_no to be safe)
          const line11 = this.schedulePG1Data.find((r: any) => r.line_no === '11');
          if (line11 && Array.isArray(line11.value)) {
            // M2_CHK = 1 means Yes, M2_CHK = 0 means No
            if (data.jsonObject[0].M2_CHK === 1) {
              if (line11.value[0]) line11.value[0].VALUE = 'Y';
              if (line11.value[1]) line11.value[1].VALUE = null;
            } else if (data.jsonObject[0].M2_CHK === 0) {
              if (line11.value[0]) line11.value[0].VALUE = null;
              if (line11.value[1]) line11.value[1].VALUE = 'N';
            }
            this.cdr.detectChanges();
          }
        }
      },
      (error: any) => {
        console.error('Error loading M2 check data:', error);
      }
    );
  }

  processScheduleOData() {
    for (let i = 1; i < this.irsF8865ScheduleOData.length; i++) {
      const item = this.irsF8865ScheduleOData[i];
      if (!item.object_id || !item.attrib_value) continue;
      
      const tempString = item.object_id;
      const tempValue = item.attrib_value;
      const attribName = tempValue.split('~')[0];
      const attribValue = (item.VALUE === '0') ? '' : item.VALUE;

      this.irsF8865ScheduleOData[i] = {
        i: item,
        tax_year: tempString.split('~')[0],
        trans_type_key: tempString.split('~')[1],
        attrib_name: attribName,
        attrib_desc: tempValue.split('~')[3],
        line_attrib_key: tempValue.split('~')[1],
        trans_details_key: tempString.split('~')[3],
        occurrence: tempString.split('~')[4],
        VALUE: item.VALUE,
        form_key: tempString.split('~')[6]
      };

      // Process Schedule O radio buttons
      if (attribName === 'LINE_1_VALUE_8865' && attribValue === 'Y') {
        this.radioBlk = 'Y';
      } else if (attribName === '8865_O_PTSP_IND_N' && attribValue === 'N') {
        this.radioBlk = 'N';
      } else if (attribName === 'LINE_1b_VALUE_8865' && attribValue === 'Y') {
        this.radioUnblk = 'Y';
      } else if (attribName === '8865_O_GAIN_DEFERRAL_N' && attribValue === 'N') {
        this.radioUnblk = 'N';
      } else if (attribName === 'LINE_3_VALUE_8865' && attribValue === 'Y') {
        this.radioUnblck = 'Y';
      } else if (attribName === '8865_O_INTGBL_PROP_N' && attribValue === 'N') {
        this.radioUnblck = 'N';
      }
    }
    
    this.cdr.detectChanges();
  }

  // Event handlers
  onFilingGroupChange(fg: any) {
    this.selectedGroup = fg;
    // Mark the attribute as changed if needed
    if (fg && fg.LEGAL_ENTITY_ID) {
      this.set_override_flag(fg.LEGAL_ENTITY_ID, '8865_NA_FILER_LEGAL_ENTITY_ID');
    }
    this.cdr.detectChanges();
  }

  updated(filingId: string) {
    this.selectedGroup = this.filingGroup.find(x => x.LEGAL_ENTITY_ID == filingId) || null;
    this.cdr.detectChanges();
  }

  onPBAChange(pba: any) {
    this.selectedGroup1 = pba;
    if (pba && pba.ACTIVITY_CODE) {
      this.set_override_flag(pba.ACTIVITY_CODE, '8865_NA_G6_PRIPL_ACTIVE_CODE');
    }
    this.cdr.detectChanges();
  }

  updated1(activityCode: string) {
    this.selectedGroup1 = this.PrincipalBusinessActivity.find(x => x.ACTIVITY_CODE == activityCode) || null;
    this.cdr.detectChanges();
  }

  selected1(leKey: any) {
    this.otherPartner = this.partnerDetails.find(x => x.LE_KEY === leKey) || null;
  }

  setOverridesFlag(value: any, attribName: string, type?: string, formType?: string) {
    const found = this.irsF8865ScheduleNAData.find((e: any) => e.attrib_name === attribName);
    if (found) {
      found.is_update = 'Y';
    }
  }

  set_override_flag(value: any, attribName: string) {
    const found = this.irsF8865ScheduleNAData.find((e: any) => e.attrib_name === attribName);
    
    if (found) {
      found.is_update = 'Y';
    }
  }

  changeCountry(countryCode: string, attribName: string, type?: string) {
    this.countryCust = countryCode;
    this.setOverridesFlag(countryCode, attribName, type);
    this.setOverridesFlag(countryCode, '8865_NA_PERSONS_BOOK_US_COUNTRY', type);
  }

  changeCountryAgent(countryCode: string, attribName: string, type?: string) {
    this.countryAgent = countryCode;
    this.setOverridesFlag(countryCode, attribName, type);
    this.setOverridesFlag(countryCode, '8865_NA_FPTSP_AGENT_US_COUNTRY', type);
  }

  changeCountryLaws(countryCode: string, attribName: string) {
    this.setOverridesFlag(countryCode, attribName, 'text');
  }

  personBnssChange(value: string, attribName: string, type: string) {
    this.radioH4Type = value;
    this.attribData['8865_NA_H4_FLAG'] = value;
    
    // If Not Applicable, clear all H4 fields
    if (value === 'N') {
      this.attribData['8865_NA_PERSONS_BOOK_NAME_1'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_NAME_2'] = null;
      this.attribData['8865_NA_H4_EIN_NUMBER'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_COUNTRY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_ADDR_1'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_ADDR_2'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_CITY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_STATE'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_ZIP'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_COUNTRY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_ADDR_1'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_ADDR_2'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_CITY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_STATE'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_ZIP'] = null;
      this.attribData['8865_NA_H4_EIN_MISSING_REASON'] = null;
      this.countryCust = '';
    }
    // If Person, clear name line 2
    if (value === 'P') {
      this.attribData['8865_NA_PERSONS_BOOK_NAME_2'] = null;
    }
    
    this.setOverridesFlag(value, attribName, type);
  }

  personBnssChangeAgent(value: string, attribName: string, type: string) {
    this.radioH3Type = value;
    this.attribData['8865_NA_H3_FLAG'] = value;
    
    // If Not Applicable, clear all H3 fields
    if (value === 'N') {
      this.attribData['8865_NA_FPTSP_AGENT_NAME_1'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_NAME_2'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_COUNTRY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_ADDR_1'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_ADDR_2'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_CITY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_STATE'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_ZIP'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_COUNTRY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_ADDR_1'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_ADDR_2'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_CITY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_STATE'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_ZIP'] = null;
      this.countryAgent = '';
    }
    // If Person, clear name line 2
    if (value === 'P') {
      this.attribData['8865_NA_FPTSP_AGENT_NAME_2'] = null;
    }
    
    this.setOverridesFlag(value, attribName, type);
  }

  radioClicked(value: string, attribName: string) {
    if (value === 'APPLD FOR' || value === 'FOREIGNUS') {
      this.attribData['8865_NA_PTSP_EIN'] = null;
    }
    this.setOverridesFlag(value, attribName, 'text');
  }

  uncheckedRadio(value: string, attribName: string) {
    if (value) {
      this.attribData['8865_NA_PTSP_EIN_MISSING_REASON'] = null;
    }
    this.setOverridesFlag(value, attribName, 'text');
  }

  clicked(value: string, attribName: string, type: string) {
    if (value === 'APPLD FOR' || value === 'FOREIGNUS') {
      this.attribData['8865_NA_H4_EIN_NUMBER'] = null;
    }
    this.setOverridesFlag(value, attribName, type);
  }

  unchecked(value: string, attribName: string, type: string) {
    if (value) {
      this.attribData['8865_NA_H4_EIN_MISSING_REASON'] = null;
    }
    this.setOverridesFlag(value, attribName, type);
  }

  // Format EIN number with dash after first 2 characters (format: XX-XXXXXXX)
  // Matches original AngularJS inputFormat directive
  formatEIN(attribName: string, event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/-/g, '').replace(/[^0-9]/g, ''); // Remove dashes and non-numeric chars
    
    // Format with dash after 2 characters
    if (value.length > 2) {
      value = value.substr(0, 2) + '-' + value.substr(2);
    }
    
    // Limit to 10 characters (including dash)
    if (value.length > 10) {
      value = value.substr(0, 10);
    }
    
    input.value = value;
    // Store without dash in model (original behavior)
    this.attribData[attribName] = value.replace(/-/g, '');
    this.cdr.detectChanges();
  }

  // Get formatted EIN for display (XX-XXXXXXX)
  getFormattedEIN(attribName: string): string {
    const value = this.attribData[attribName];
    if (value && value.length > 2) {
      return value.substr(0, 2) + '-' + value.substr(2);
    }
    return value || '';
  }

  updateWeightedAverage(currencyCode: string) {
    // Implementation depends on backend logic
  }

  onRadioBtnSelection(row: any, index: number, value: string) {
    // Matches AngularJS vm.onRadioBtnSelection exactly (lines 1725-1731)
    // AngularJS: when Y selected, set N value to null (and vice versa)
    // The selected value is set by ng-model in AngularJS; here we set it manually
    if (value === 'Y') {
      if (Array.isArray(row.value)) {
        if (row.value[0]) row.value[0].VALUE = 'Y';
        if (row.value[1]) row.value[1].VALUE = null;  // Match AngularJS: null, not ''
      }
    } else if (value === 'N') {
      if (Array.isArray(row.value)) {
        if (row.value[0]) row.value[0].VALUE = null;  // Match AngularJS: null, not ''
        if (row.value[1]) row.value[1].VALUE = 'N';
      }
    }
    
    // Mark as updated
    row.isUpdate = 'Y';
    
    // Apply validation logic from original AngularJS component
    this.checkingForValidation(row, index, value);
  }

  /**
   * Handler for text input changes in Schedule PG1 (lines 8A, 9, etc.)
   * Marks the row as updated so it gets saved with override_y_n = 'Y'
   */
  onPG1TextChange(row: any) {
    row.isUpdate = 'Y';
  }

  // Validation logic matching original AngularJS component
  checkingForValidation(row: any, index: number, val?: string) {
    // When line 5 No is selected, disable the amount text field
    if (row.line_no === '5') {
      if (row.value[1]?.VALUE === 'N') {
        const line5Row = this.schedulePG1Data.find((r: any) => r.line_no === '5');
        if (line5Row && line5Row.value_1) {
          line5Row.value_1.VALUE = null;
          line5Row.isEditableFlagText = 'N';
        }
      } else if (row.value[0]?.VALUE === 'Y') {
        const line5Row = this.schedulePG1Data.find((r: any) => r.line_no === '5');
        if (line5Row) {
          line5Row.isEditableFlagText = 'Y';
        }
      }
    }
    
    // When line 10a is changed, disable 10b and 11 (they cannot be edited)
    if (row.line_no === '10a') {
      const line10b = this.schedulePG1Data.find((r: any) => r.line_no === '10b');
      const line11 = this.schedulePG1Data.find((r: any) => r.line_no === '11');
      
      if (line10b) {
        line10b.isEditableFlag = 'N';
      }
      if (line11) {
        line11.isEditableFlag = 'N';
      }
    }
    
    this.cdr.detectChanges();
  }

  /**
   * Main save method - builds all editrows (PG1 + NA + O) and saves in a single request.
   */
  saveScheduleGForm() {
    this.crudLoading = true;

    if (!this.validatePG1Data()) {
      this.crudLoading = false;
      return;
    }

    this.prepareAttribDataForSave();
    this.prepareScheduleOAttribData();

    const allEditRows: any[][] = [
      ...this.buildPG1EditRows(),
      ...this.buildNAEditRows(),
      ...this.buildOEditRows()
    ];

    const scheduleSettings = [{
      tax_year: this.getCorrectTaxYear(),
      scenario: this.getCorrectScenario(),
      jcd_key: this.globalService.jcd_key || 250
    }];

    const scheduleDetails = { addrows: [], editrows: allEditRows, removerows: [] };

    this.form8865Service.saveScheduleOPG(
      this.getBaseUrl(),
      scheduleSettings,
      scheduleDetails,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.crudLoading = false;
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('Form 8865 saved successfully', 'gtw-snackbar--green');
          this.reloadAllDataAfterSave();
        }
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error saving Form 8865. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  /**
   * Validate PG1 data before saving
   * Matches vm.checkingForValidSave in AngularJS
   */
  private validatePG1Data(): boolean {
    if (this.schedulePG1Data.length > 0) {
      const line5 = this.schedulePG1Data[0];
      if (line5 && line5.value && line5.value.VALUE === 'Y') {
        if (!line5.value_1 || !line5.value_1.VALUE) {
          this.gtwSnackbarService.openSnackBar(
            'Error saving Schedule PG1 data - Check values for line 5 amount column.',
            'gtw-snackbar--red'
          );
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Build PG1 editrows (lines 5-15b with radio buttons)
   */
  private buildPG1EditRows(): any[][] {
    const editRow: any[][] = [];
    if (this.schedulePG1Data.length === 0) return editRow;

    const temporaryObject = this.schedulePG1Data;

    for (let loopIndex = 0; loopIndex < temporaryObject.length; loopIndex++) {
      const item = temporaryObject[loopIndex];

      if (loopIndex === 0 && item) {
        if (item.value && item.value.attrib_name) {
          editRow.push([{
            tax_year: this.getCorrectTaxYear(),
            trans_type_key: item.saveParams?.trans_type_key,
            combination_key: this.rowData.HO_COMBINATION_KEY,
            group_obj_key: this.rowData.GROUP_OBJ_KEY,
            form_key: item.saveParams?.form_key,
            line_no: item.line_no,
            occurence: item.saveParams?.occurrence === '0' ? null : item.saveParams?.occurrence,
            line_attrib_key: item.value.line_attrib_key,
            Attribute_Name: item.value.attrib_name,
            Attribute_Value: item.value.VALUE,
            trans_dtls_key: item.saveParams?.trans_details_key === '0' ? null : item.saveParams?.trans_details_key,
            trans_status: 'A',
            override_y_n: item.isUpdate === 'Y' && item.value.VALUE ? 'Y' : 'N'
          }]);
        }

        if (item.value_1 && item.value_1.attrib_name) {
          const amountValue = item.value_1.VALUE != null ? String(item.value_1.VALUE) : item.value_1.VALUE;
          editRow.push([{
            tax_year: this.getCorrectTaxYear(),
            trans_type_key: item.saveParams?.trans_type_key,
            combination_key: this.rowData.HO_COMBINATION_KEY,
            group_obj_key: this.rowData.GROUP_OBJ_KEY,
            form_key: item.saveParams?.form_key,
            line_no: item.line_no,
            occurence: item.saveParams?.occurrence === '0' ? null : item.saveParams?.occurrence,
            line_attrib_key: item.value_1.line_attrib_key,
            Attribute_Name: item.value_1.attrib_name,
            Attribute_Value: amountValue,
            trans_dtls_key: item.saveParams?.trans_details_key === '0' ? null : item.saveParams?.trans_details_key,
            trans_status: 'A',
            override_y_n: item.isUpdate === 'Y' && item.value_1.VALUE ? 'Y' : 'N'
          }]);
        }
      }

      if (item.propertyType && String(item.propertyType) === '13' && Array.isArray(item.attrib_name)) {
        for (let k = 0; k < item.attrib_name.length; k++) {
          editRow.push([{
            tax_year: this.getCorrectTaxYear(),
            trans_type_key: item.saveParams?.trans_type_key,
            combination_key: this.rowData.HO_COMBINATION_KEY,
            group_obj_key: this.rowData.GROUP_OBJ_KEY,
            form_key: item.saveParams?.form_key,
            line_no: item.line_no,
            occurence: item.saveParams?.occurrence === '0' ? null : item.saveParams?.occurrence,
            line_attrib_key: item.value[k]?.line_attrib_key,
            Attribute_Name: item.attrib_name[k],
            Attribute_Value: item.value[k]?.VALUE !== undefined ? item.value[k]?.VALUE : null,
            trans_dtls_key: item.saveParams?.trans_details_key === '0' ? null : item.saveParams?.trans_details_key,
            trans_status: 'A',
            override_y_n: item.isUpdate === 'Y' && !!item.value[k]?.VALUE ? 'Y' : 'N'
          }]);
        }
      } else if (item.attrib_name && !Array.isArray(item.attrib_name) && item.value) {
        let attrValue = item.value.VALUE;
        if (attrValue != null && typeof attrValue === 'number') {
          attrValue = String(attrValue);
        }
        editRow.push([{
          tax_year: this.getCorrectTaxYear(),
          trans_type_key: item.saveParams?.trans_type_key,
          combination_key: this.rowData.HO_COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: item.saveParams?.form_key,
          line_no: item.line_no,
          occurence: item.saveParams?.occurrence === '0' ? null : item.saveParams?.occurrence,
          line_attrib_key: item.value.line_attrib_key,
          Attribute_Name: item.attrib_name,
          Attribute_Value: attrValue,
          trans_dtls_key: item.saveParams?.trans_details_key === '0' ? null : item.saveParams?.trans_details_key,
          trans_status: 'A',
          override_y_n: item.isUpdate === 'Y' && !!item.value.VALUE ? 'Y' : 'N'
        }]);
      }
    }

    return editRow;
  }

  /**
   * Build NA editrows (main textual form)
   */
  private buildNAEditRows(): any[][] {
    const editRow: any[][] = [];
    if (this.irsF8865ScheduleNAData.length === 0) return editRow;

    const temporaryObject = this.irsF8865ScheduleNAData;

    for (let loopIndex = 1; loopIndex < temporaryObject.length; loopIndex++) {
      const item = temporaryObject[loopIndex];
      if (!item.attrib_name) continue;

      let attrValue = this.attribData[item.attrib_name];
      if (attrValue != null && typeof attrValue === 'number') {
        attrValue = String(attrValue);
      }

      const saveObject: any = [{
        tax_year: this.getCorrectTaxYear(),
        trans_type_key: item.trans_type_key,
        combination_key: this.rowData.HO_COMBINATION_KEY,
        group_obj_key: this.rowData.GROUP_OBJ_KEY,
        form_key: item.form_key,
        line_no: item.i?.line_no,
        occurence: item.occurrence === '0' ? null : item.occurrence,
        line_attrib_key: item.line_attrib_key,
        Attribute_Name: item.attrib_name,
        Attribute_Value: attrValue !== undefined ? attrValue : null,
        trans_dtls_key: item.trans_details_key === '0' ? null : item.trans_details_key,
        trans_status: 'A',
        override_y_n: item.is_update === 'Y' ? 'Y' : 'N'
      }];

      editRow.push(saveObject);
    }

    return editRow;
  }

  /**
   * Prepare attribData before saving
   * Matches the preparation logic in vm.saveForm() in AngularJS
   */
  private prepareAttribDataForSave() {
    // Format dates
    const dateFields = [
      '8865_NA_FILER_BEGIN_DATE',
      '8865_NA_FILER_END_DATE', 
      '8865_NA_PTSP_BEGIN_DATE',
      '8865_NA_PTSP_END_DATE',
      '8865_NA_G4_DATE'
    ];
    
    dateFields.forEach(field => {
      if (this.attribData[field]) {
        const date = this.attribData[field];
        if (date instanceof Date) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          this.attribData[field] = `${month}/${day}/${year}`;
        }
      }
    });

    // Handle country logic for partnership address
    if (this.countryVar === 'US') {
      this.attribData['8865_NA_PTSP_US_COUNTRY'] = this.countryVar;
      this.attribData['8865_NA_PTSP_F_COUNTRY'] = null;
      this.attribData['8865_NA_PTSP_F_ADDR_1'] = null;
      this.attribData['8865_NA_PTSP_F_ADDR_2'] = null;
      this.attribData['8865_NA_PTSP_F_CITY'] = null;
      this.attribData['8865_NA_PTSP_F_STATE'] = null;
      this.attribData['8865_NA_PTSP_F_ZIP'] = null;
    } else if (this.countryVar) {
      this.attribData['8865_NA_PTSP_F_COUNTRY'] = this.countryVar;
      this.attribData['8865_NA_PTSP_US_COUNTRY'] = null;
      this.attribData['8865_NA_PTSP_US_ADDR_1'] = null;
      this.attribData['8865_NA_PTSP_US_ADDR_2'] = null;
      this.attribData['8865_NA_PTSP_US_CITY'] = null;
      this.attribData['8865_NA_PTSP_US_STATE'] = null;
      this.attribData['8865_NA_PTSP_US_ZIP'] = null;
    }

    // Handle country logic for agent address (H.3)
    if (this.countryAgent === 'US') {
      this.attribData['8865_NA_FPTSP_AGENT_US_COUNTRY'] = this.countryAgent;
      this.attribData['8865_NA_FPTSP_AGENT_F_COUNTRY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_ADDR_1'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_ADDR_2'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_CITY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_STATE'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_F_ZIP'] = null;
    } else if (this.countryAgent) {
      this.attribData['8865_NA_FPTSP_AGENT_F_COUNTRY'] = this.countryAgent;
      this.attribData['8865_NA_FPTSP_AGENT_US_COUNTRY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_ADDR_1'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_ADDR_2'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_CITY'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_STATE'] = null;
      this.attribData['8865_NA_FPTSP_AGENT_US_ZIP'] = null;
    }

    // Handle country logic for books custodian address (H.4)
    if (this.countryCust === 'US') {
      this.attribData['8865_NA_PERSONS_BOOK_US_COUNTRY'] = this.countryCust;
      this.attribData['8865_NA_PERSONS_BOOK_F_COUNTRY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_ADDR_1'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_ADDR_2'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_CITY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_STATE'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_F_ZIP'] = null;
    } else if (this.countryCust) {
      this.attribData['8865_NA_PERSONS_BOOK_F_COUNTRY'] = this.countryCust;
      this.attribData['8865_NA_PERSONS_BOOK_US_COUNTRY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_ADDR_1'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_ADDR_2'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_CITY'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_STATE'] = null;
      this.attribData['8865_NA_PERSONS_BOOK_US_ZIP'] = null;
    }

    // Handle other partner selection
    if (this.otherPartner) {
      this.attribData['8865_NA_OTHER_PARTNER'] = this.otherPartner.LE_KEY;
    }

    // Handle filing group selection and filer address
    if (this.selectedGroup) {
      if (this.selectedGroup.REG_ADDRESS_COUNTRY_8865 === 'US') {
        this.attribData['8865_NA_FILER_US_ADDR_1'] = this.selectedGroup.REG_ADDRESS_LINE1_8865;
        this.attribData['8865_NA_FILER_US_ADDR_2'] = this.selectedGroup.REG_ADDRESS_LINE2_8865;
        this.attribData['8865_NA_FILER_US_CITY'] = this.selectedGroup.REG_ADDRESS_CITY;
        this.attribData['8865_NA_FILER_US_STATE'] = this.selectedGroup.REG_ADDRESS_STATE;
        this.attribData['8865_NA_FILER_US_COUNTRY'] = this.selectedGroup.REG_ADDRESS_COUNTRY_8865;
        this.attribData['8865_NA_FILER_US_ZIP'] = this.selectedGroup.REG_ADDRESS_ZIP;
        this.attribData['8865_NA_FILER_F_ADDR_1'] = null;
        this.attribData['8865_NA_FILER_F_ADDR_2'] = null;
        this.attribData['8865_NA_FILER_F_CITY'] = null;
        this.attribData['8865_NA_FILER_F_STATE'] = null;
        this.attribData['8865_NA_FILER_F_COUNTRY'] = null;
        this.attribData['8865_NA_FILER_F_ZIP'] = null;
      } else {
        this.attribData['8865_NA_FILER_F_ADDR_1'] = this.selectedGroup.REG_ADDRESS_LINE1_8865;
        this.attribData['8865_NA_FILER_F_ADDR_2'] = this.selectedGroup.REG_ADDRESS_LINE2_8865;
        this.attribData['8865_NA_FILER_F_CITY'] = this.selectedGroup.REG_ADDRESS_CITY;
        this.attribData['8865_NA_FILER_F_STATE'] = this.selectedGroup.REG_ADDRESS_STATE;
        this.attribData['8865_NA_FILER_F_COUNTRY'] = this.selectedGroup.REG_ADDRESS_COUNTRY_8865;
        this.attribData['8865_NA_FILER_F_ZIP'] = this.selectedGroup.REG_ADDRESS_ZIP;
        this.attribData['8865_NA_FILER_US_ADDR_1'] = null;
        this.attribData['8865_NA_FILER_US_ADDR_2'] = null;
        this.attribData['8865_NA_FILER_US_CITY'] = null;
        this.attribData['8865_NA_FILER_US_STATE'] = null;
        this.attribData['8865_NA_FILER_US_COUNTRY'] = null;
        this.attribData['8865_NA_FILER_US_ZIP'] = null;
      }
      
      this.attribData['8865_NA_FILER_BEGIN_DATE'] = this.selectedGroup.FILER_TAX_YEAR_BEGIN;
      this.attribData['8865_NA_FILER_END_DATE'] = this.selectedGroup.FILER_TAX_YEAR_END;
      this.attribData['8865_NA_FILER_NAME_1'] = this.selectedGroup.LEGAL_ENTITY_NAME;
      this.attribData['8865_NA_FILER_LEGAL_ENTITY_ID'] = this.selectedGroup.LEGAL_ENTITY_ID;
      this.attribData['8865_NA_FILER_EIN'] = this.selectedGroup.FILER_EIN ? 
        this.selectedGroup.FILER_EIN.replace('-', '') : null;
    }

    // Handle principal business activity selection
    if (this.selectedGroup1) {
      this.attribData['8865_NA_G7_PRIPL_ACTIVITY'] = this.selectedGroup1.ACTIVITY_DESC;
      this.attribData['8865_NA_G6_PRIPL_ACTIVE_CODE'] = this.selectedGroup1.ACTIVITY_CODE;
    }

    // Handle checkbox states (convert boolean to '1' or null)
    this.attribData['8865_NA_A_1_Y'] = this.checkBoxb1 ? '1' : null;
    this.attribData['8865_NA_A_1_N'] = !this.checkBoxb1 ? '0' : null;
    this.attribData['8865_NA_A_2_Y'] = this.checkBoxb3 ? '1' : null;
    this.attribData['8865_NA_A_2_N'] = !this.checkBoxb3 ? '0' : null;
    this.attribData['8865_NA_A_3_Y'] = this.checkBoxb4 ? '1' : null;
    this.attribData['8865_NA_A_3_N'] = !this.checkBoxb4 ? '0' : null;
    this.attribData['8865_NA_A_4_Y'] = this.checkBoxb5 ? '1' : null;
    this.attribData['8865_NA_A_4_N'] = !this.checkBoxb5 ? '0' : null;
    
    this.attribData['8865_NA_F1042_Y'] = this.form_1042 ? '1' : null;
    this.attribData['8865_NA_F1042_N'] = !this.form_1042 ? '0' : null;
    this.attribData['8865_NA_F8804_Y'] = this.form_8804 ? '1' : null;
    this.attribData['8865_NA_F8804_N'] = !this.form_8804 ? '0' : null;
    this.attribData['8865_NA_F1065_Y'] = this.form_1065 ? '1' : null;
    this.attribData['8865_NA_F1065_N'] = !this.form_1065 ? '0' : null;
    
    this.attribData['8865_NA_PARTNER_CATG_1_Y'] = this.checkCatg1 ? '1' : null;
    this.attribData['8865_NA_PARTNER_CATG_1_N'] = !this.checkCatg1 ? '0' : null;
    this.attribData['8865_NA_PARTNER_CATG_2_Y'] = this.checkCatg2 ? '1' : null;
    this.attribData['8865_NA_PARTNER_CATG_2_N'] = !this.checkCatg2 ? '0' : null;
    this.attribData['8865_NA_PARTNER_CONSTRUCTIVE_OWNER_Y'] = this.checkConstOwner ? '1' : null;
    this.attribData['8865_NA_PARTNER_CONSTRUCTIVE_OWNER_N'] = !this.checkConstOwner ? '0' : null;
  }

  /**
   * Prepare Schedule O attrib values based on radio button states.
   */
  private prepareScheduleOAttribData() {
    if (this.radioBlk === 'Y') {
      this.attribData['LINE_1_VALUE_8865'] = 'Y';
      this.attribData['8865_O_PTSP_IND_N'] = null;
    } else {
      this.attribData['8865_O_PTSP_IND_N'] = this.radioBlk;
      this.attribData['LINE_1_VALUE_8865'] = null;
    }

    if (this.radioUnblk === 'Y') {
      this.attribData['LINE_1b_VALUE_8865'] = 'Y';
      this.attribData['8865_O_GAIN_DEFERRAL_N'] = null;
    } else {
      this.attribData['8865_O_GAIN_DEFERRAL_N'] = this.radioUnblk;
      this.attribData['LINE_1b_VALUE_8865'] = null;
    }

    if (this.radioUnblck === 'Y') {
      this.attribData['LINE_3_VALUE_8865'] = 'Y';
      this.attribData['8865_O_INTGBL_PROP_N'] = null;
    } else {
      this.attribData['8865_O_INTGBL_PROP_N'] = this.radioUnblck;
      this.attribData['LINE_3_VALUE_8865'] = null;
    }
  }

  /**
   * Build Schedule O editrows (questions 1-3 radio buttons).
   */
  private buildOEditRows(): any[][] {
    const editRow: any[][] = [];
    if (this.irsF8865ScheduleOData.length === 0) return editRow;

    const attribsToSave = [
      'LINE_1_VALUE_8865', '8865_O_PTSP_IND_N',
      'LINE_1b_VALUE_8865', '8865_O_GAIN_DEFERRAL_N',
      'LINE_3_VALUE_8865', '8865_O_INTGBL_PROP_N'
    ];

    attribsToSave.forEach(attribName => {
      const item = this.irsF8865ScheduleOData.find((x: any) => x.attrib_name === attribName);
      if (item) {
        item.VALUE = this.attribData[attribName];
        editRow.push([{
          tax_year: this.getCorrectTaxYear(),
          trans_type_key: item.trans_type_key,
          combination_key: this.rowData.HO_COMBINATION_KEY,
          group_obj_key: this.rowData.GROUP_OBJ_KEY,
          form_key: item.form_key,
          line_no: item.i?.line_no,
          occurence: item.occurrence === '0' ? null : item.occurrence,
          line_attrib_key: item.line_attrib_key,
          Attribute_Name: item.attrib_name,
          Attribute_Value: this.attribData[item.attrib_name],
          trans_dtls_key: item.trans_details_key === '0' ? null : item.trans_details_key,
          trans_status: 'A'
        }]);
      }
    });

    return editRow;
  }

  /**
   * Reload form data after successful save.
   * Matches AngularJS which only calls getFormData() after save.
   */
  private reloadAllDataAfterSave() {
    this.loadFormData();
    this.requestGridUpdate.emit('refresh');
  }

  reset() {
    this.isInitialized = false;
    this.isLoading = false;
    this.lastContextKey = '';
    this.tryInit();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Helper methods for template
  isUSCountry(countryType: string): boolean {
    switch (countryType) {
      case 'var':
        return this.countryVar === 'US';
      case 'cust':
        return this.countryCust === 'US';
      case 'agent':
        return this.countryAgent === 'US';
      default:
        return false;
    }
  }

  formatDate(date: Date | string | null): string {
    if (!date) return '';
    // If date is already a Date object, use it directly
    // If it's a string, parse it as local date to avoid timezone shift
    const d = date instanceof Date ? date : parseAsLocalDate(date);
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  }

  // Helper method for date input changes (can't use 'new' in template)
  // Uses parseAsLocalDate to avoid timezone shift issues
  onDateChange(value: string, attribName: string, flagMethod: 'set' | 'override' = 'set') {
    this.attribData[attribName] = parseAsLocalDate(value);
    if (flagMethod === 'set') {
      this.set_override_flag(value, attribName);
    } else {
      this.setOverridesFlag(value, attribName, 'text');
    }
  }

  /**
   * Format Date object to YYYY-MM-DD string for input[type="date"]
   * Uses local date components directly to avoid timezone shift issues.
   * 
   * Angular's date pipe can cause timezone issues because it converts
   * the Date's internal UTC representation back to local time, which
   * can shift the date by a day in certain timezones.
   * 
   * This method extracts local date components directly without any
   * timezone conversion.
   */
  formatDateForInput(date: Date | string | null | undefined): string {
    if (!date) return '';
    
    // If it's a string, parse it first
    const dateObj = date instanceof Date ? date : parseAsLocalDate(date);
    if (!dateObj || isNaN(dateObj.getTime())) return '';
    
    // Extract local date components directly (no timezone conversion)
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }
}
